import { SolidButton } from "@components/atoms/base-button";
import { Seo } from "@shopify/hydrogen";
import { translate } from "@locales";
import type { Product as IProduct } from "@shopify/hydrogen/storefront-api-types";
import { Markup } from "@components/atoms/markup/index.client";
import { ViewOnlyProductProps } from "@components/molecules/product/viewOnlyProduct";
import type { Product } from "@shopify/hydrogen/storefront-api-types";
import { Separator } from "@components/atoms/separator/index.client";
import { Link, Money, ProductOptionsProvider } from "@shopify/hydrogen";
import { ReactNode} from "react";
import { PurchaseMarkup } from "@components/molecules/product/baseProduct/components/index.client";

export interface BaseProductProps {
  product?: Product;
  aboutStockText: ReactNode;
  addToCardButton: boolean;
  onRedirect?: () => void;
}

export function useBaseProduct(product: any) {
  const { priceV2: price, compareAtPriceV2: compareAtPrice } =
    product?.variants?.nodes[0];

  const isDiscounted = Object.keys(compareAtPrice ?? {}).length
    ? Number(compareAtPrice?.amount) > Number(price?.amount)
    : null;

  const imgAttributes = 
     {
      src: product?.media?.nodes[0].image.url,
      alt: product?.media?.nodes[0].image.altText,
    };
 

  return {
    models: {
      title: product?.title,
      imgURL: imgAttributes.src,
      price,
      compareAtPrice,
      isDiscounted,
      imgAlt: imgAttributes.alt,
    },
  };
}
export default function BaseProduct(props: BaseProductProps): JSX.Element {
  const { addToCardButton, product, onRedirect } = props;

  const { models } = useBaseProduct(product);

  return (
    <ProductOptionsProvider data={product!}>
      <div className="py-4 grid content-start justify-items-center border-solid border border-custom-gray-50 max-w-[335px] w-full lg:w-64 h-[500px]">
        <Link
          onClick={onRedirect}
          className="flex flex-col"
          to={`/products/${product?.handle}`}
        >
          <div className="my-2 w-full h-60">
            <img
              className=" object-contain w-full h-full"
              src={models.imgURL}
              alt={models.imgAlt}
              loading="lazy"
            />
          </div>

          <span className="h-[70px] my-1 mx-4 text-center text-clip overflow-hidden font-chivo-regular break-words uppercase tracking-wider">
            {models.title}
          </span>
        </Link>

        <div className="flex gap-2">
          {models.isDiscounted && (
            <Money
              className="my-3 text-gray-200 line-through font-chivo-regular font-medium text-xl tracking-wider"
              data={models.compareAtPrice}
            />
          )}
          <Money
            className="my-3 text-custom-dark-blue font-chivo-regular font-medium text-xl tracking-wider"
            data={models.price}
          />
        </div>
        {addToCardButton && <PurchaseMarkup product={product} />}
      </div>
    </ProductOptionsProvider>
  );
}

function MarkupRender({ product }) {
  const { totalInventory } = product;

  if (totalInventory === 0) {
    return (
      <Markup color="grey" background>
        Out of stock
      </Markup>
    );
  }

  if (totalInventory <= 10) {
    return (
      <Markup color="yellow">
        Only {totalInventory} left!
      </Markup>
    );
  }

  return <div className="invisible"/>
}

export function Product(props: ViewOnlyProductProps) {
  const { product } = props;
  return (
    <BaseProduct
      {...props}
      aboutStockText={<MarkupRender product={product} />}
      addToCardButton
    />
  );
}


export function ShopOurSets({ products, title }) {
  return (
    <>
      <Seo type="product" data={products as any} />

      <div className="w-full flex flex-col items-center mt-36">
        <h1 className="font-chivo-bold text-center uppercase text-3xl lg:text-h1 lg:mb-8">
          {title}
        </h1>
        <div className={`flex flex-wrap gap-5 m-5`}>
          {products.nodes.map((product: IProduct) => (
            <Product key={product.id} product={product} />
          ))}
        </div>
        <Link to="/shop">
          <SolidButton className="w-[200px] h-[50px] mt-5">
            {translate("home.shop-our-sets.button")}
          </SolidButton>
        </Link>
      </div>
    </>
  );
}
